import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { Editor, Node, Range, Path } from 'slate';
import ListUtils from '../editor-utils/block-element-utils/list-utils';
import { isRangeCollapsed } from '../editor-utils/range-utils';
import { SfEditor } from '../custom/custom';
import CodeUtils from '../editor-utils/block-element-utils/code-utils';
import TextUtils from '../editor-utils/text-utils';
import TableUtils from '../editor-utils/block-element-utils/table-utils';
import BlockquoteUtils from '../editor-utils/block-element-utils/blockquote-utils';
import { clearBlockFormat, clearMarkerFormat } from '../editor-utils/clear-format-utils';
var SHORTCUTS = {
  '1.': 'ordered_list',
  '*': 'unordered_list',
  '-': 'unordered_list',
  '>': 'blockquote',
  '#': 'header_one',
  '##': 'header_two',
  '###': 'header_three',
  '####': 'header_four',
  '#####': 'header_five',
  '######': 'header_six',
  '   ': 'code_block'
};
var withMarkdownShortcut = function withMarkdownShortcut(editor) {
  var exec = editor.exec;
  var listUtils = new ListUtils(editor);
  var codeUtils = new CodeUtils(editor);
  var tableUtils = new TableUtils(editor);
  var textUtils = new TextUtils(editor);
  var blockquoteUtils = new BlockquoteUtils(editor);
  editor.exec = function (command) {
    var selection = editor.selection;
    switch (command.type) {
      case 'split_list_item':
        Editor.splitNodes(editor, {
          match: 'block',
          always: true
        });
        break;
      case 'insert_break':
        if (!Range.isCollapsed(editor.selection)) {
          var _node = Editor.node(editor, [editor.selection.anchor.path[0]]);
          if (_node && _node[0].type.includes('header')) {
            exec(command);
            Editor.setNodes(editor, {
              type: 'paragraph'
            });
            return;
          }
          // if the expand selection is in table delete the selected and insert a new row when press enter
          if (tableUtils.isInTable()) {
            Editor.collapse(editor, {
              edge: 'end'
            });
            tableUtils.insertRow();
            return;
          }
          exec(command);
          return;
        }
        var _Editor$nodes = Editor.nodes(editor, {
            match: {
              type: 'link'
            }
          }),
          _Editor$nodes2 = _slicedToArray(_Editor$nodes, 1),
          link = _Editor$nodes2[0];

        // when selection is at the end of link, select the text node after the link
        // to prevent split a empty link node
        if (link) {
          var linkPath = link[1];
          if (Editor.isEnd(editor, editor.selection.anchor, linkPath)) {
            var nextPath = Path.next(linkPath);
            var point = {
              path: nextPath,
              offset: 0
            };
            Editor.select(editor, point);
          }
        }

        // When a picture is selected, a blank line is inserted when press Enter
        var _Editor$nodes3 = Editor.nodes(editor, {
            match: {
              type: 'image'
            }
          }),
          _Editor$nodes4 = _slicedToArray(_Editor$nodes3, 1),
          image = _Editor$nodes4[0];
        if (image) {
          var imagePath = image[1].slice();
          var imageIndex = imagePath.pop();
          Editor.insertNodes(editor, {
            text: ''
          }, {
            at: [].concat(_toConsumableArray(imagePath), [imageIndex + 1])
          });
          // insert new table row when selection is in table
          if (tableUtils.isInTable()) {
            tableUtils.insertRow();
            return;
          }
          exec(command);
          return;
        }
        // unwrap blockquote when press enter at an empty blockquote line;
        if (blockquoteUtils.isInBlockquote()) {
          var _Editor$nodes5 = Editor.nodes(editor, {
              match: 'block'
            }),
            _Editor$nodes6 = _slicedToArray(_Editor$nodes5, 1),
            block = _Editor$nodes6[0];
          if (Node.text(block[0]).length === 0) {
            Editor.unwrapNodes(editor, {
              match: {
                type: 'blockquote'
              },
              split: true
            });
            return;
          }
        }
        // if press enter in a list unwrap the list or insert a new list item
        if (listUtils.isInlist()) {
          var _node2 = SfEditor.getNodesByTypeAtRange(editor, 'list_item');
          // unwrap list if the list item text is none
          if (Node.text(_node2[0]).length === 0) {
            listUtils.unwrapList();
            return;
          }
          // insert a new list item
          if (isRangeCollapsed(editor.selection)) {
            SfEditor.splitNodesAtPoint(editor, {
              at: editor.selection.anchor,
              match: {
                type: 'list_item'
              },
              always: true
            });
            return;
          }
        }
        if (codeUtils.isInCodeBlock()) {
          codeUtils.splitCodeLine();
          return;
        }
        if (tableUtils.isInTable()) {
          tableUtils.insertRow();
          return;
        }
        var node = Editor.node(editor, [editor.selection.anchor.path[0]]);
        if (node && node[0].type.includes('header')) {
          var header = node[0];
          // get the offset of the selection
          var offset = editor.selection.anchor.offset;
          // split the header
          exec(command);
          // if the selection is at the end of the header
          // set the splited header sibling to paragraph 
          if (offset === Node.text(header).length) {
            Editor.setNodes(editor, {
              type: 'paragraph'
            });
          }
          return;
        }
        var _Editor$nodes7 = Editor.nodes(editor, {
            match: {
              type: 'column'
            }
          }),
          _Editor$nodes8 = _slicedToArray(_Editor$nodes7, 1),
          column = _Editor$nodes8[0];
        if (column) {
          var columnPath = column[1];
          if (Editor.isEnd(editor, editor.selection.anchor, columnPath)) {
            var _nextPath = Path.next(columnPath);
            var _point = {
              path: _nextPath,
              offset: 0
            };
            Editor.select(editor, _point);
          }
        }
        exec(command);
        break;
      case 'insert_text':
        // set element by markdown shortcut;
        if (command.text === ' ' && isRangeCollapsed(selection)) {
          // if current selection is in table or code block exec default command
          if (tableUtils.isInTable() || codeUtils.isInCodeBlock()) {
            exec(command);
            return;
          }
          var _textUtils$getTextInf = textUtils.getTextInfoBeforeSelection(),
            beforeText = _textUtils$getTextInf.beforeText,
            range = _textUtils$getTextInf.range,
            _block = _textUtils$getTextInf.block;
          var path = _block[1];
          var type = SHORTCUTS[beforeText];
          // set block element by shortcut
          if (type) {
            Editor.select(editor, range);
            Editor.delete(editor);
            if (type === 'blockquote') {
              if (!blockquoteUtils.isInBlockquote()) {
                editor.exec({
                  type: 'set_blockquote'
                });
              }
            }
            if (type.includes('header')) {
              // set the outest  paragraph into header 
              if (_block[0].type === 'paragraph' && path.length === 1) {
                editor.exec({
                  type: 'set_header',
                  headerType: type
                });
              }
            }
            if (type === 'ordered_list') {
              editor.exec({
                type: 'set_ordered_list'
              });
            }
            if (type === 'unordered_list') {
              editor.exec({
                type: 'set_unordered_list'
              });
            }
            if (type === 'code_block') {
              if (!codeUtils.isInCodeBlock()) {
                editor.exec({
                  type: 'set_code_block'
                });
                return;
              }
            }
            return;
          } else {
            // set text mark by shortcut
            textUtils.setTextMarkByShortCut(exec, command);
            return;
          }
        }
        if (command.text === '`' && isRangeCollapsed(editor.selection)) {
          var _textUtils$getTextInf2 = textUtils.getTextInfoBeforeSelection(),
            _beforeText = _textUtils$getTextInf2.beforeText,
            _range = _textUtils$getTextInf2.range;
          if (_beforeText === '``' && !codeUtils.isInCodeBlock()) {
            Editor.select(editor, _range);
            Editor.delete(editor);
            editor.exec({
              type: 'set_code_block'
            });
            return;
          }
        }
        exec(command);
        break;
      case 'increase_list_item_depth':
        listUtils.increaseListDepth();
        break;
      case 'delete_backward':
        var anchor = selection.anchor;
        if (anchor.offset === 0 && isRangeCollapsed(selection)) {
          var _Editor$nodes9 = Editor.nodes(editor, {
              match: 'block'
            }),
            _Editor$nodes10 = _slicedToArray(_Editor$nodes9, 1),
            match = _Editor$nodes10[0];
          var _block2 = match[0];
          // set header to paragraph when delete at the start of header
          if (_block2.type.includes('header')) {
            editor.exec({
              type: 'set_header',
              headerType: 'paragraph'
            });
            return;
          }

          /**
           * If the anchor offset is 0 but not focus the head of the block node such as
           * an image is selected, the editor shoud delete the image first instead of
           * execute the delete operation of list or code_block or table
           */
          if (_block2.children.length > 1) {
            var blockDepth = match[1].length - 1;
            var inlineIndex = anchor.path[blockDepth + 1];
            // If the inline node is the first child of the block, execute default command
            if (inlineIndex !== 0) {
              exec(command);
              return;
            }
          }
          if (blockquoteUtils.isInBlockquote()) {
            editor.exec({
              type: 'unwrap_blockquote'
            });
            return;
          }
          if (listUtils.isInlist()) {
            var _Editor$nodes11 = Editor.nodes(editor, {
                match: [{
                  type: 'list_item'
                }]
              }),
              _Editor$nodes12 = _slicedToArray(_Editor$nodes11, 1),
              _node3 = _Editor$nodes12[0];
            var listItemPath = _node3[1];
            // Unwrap the list item when the selection is at the first list item
            if (listItemPath.at(-1) === 0 && anchor.path[listItemPath.length] === 0) {
              listUtils.unwrapList();
              return;
            }

            // List items with mutiple chidren
            var currentChildBlockIndex = anchor.path[anchor.path.length - 2];
            var currentListItem;
            for (var i = anchor.path.length - 1; i > 0; i--) {
              var _node4 = Node.get(editor, anchor.path.slice(0, i));
              if (_node4.type === 'list_item') {
                currentListItem = _node4;
                break;
              }
            }
            if (currentChildBlockIndex === 0 && currentListItem.children.length > 1) {
              Editor.withoutNormalizing(editor, function (editor) {
                listUtils.unwrapList();
                exec(command);
              });
              return;
            }
          }
          if (codeUtils.isInCodeBlock()) {
            var _node5 = Editor.match(editor, editor.selection, {
              type: 'code_block'
            });
            var codeBlock = _node5[0];
            var children = codeBlock.children;
            var text = Node.text(codeBlock);
            if (text.length === 0 && children.length === 1) {
              editor.exec({
                type: 'unwrap_code_block'
              });
              return;
            }
          }
          if (tableUtils.isInTable()) {
            var _block3 = Editor.match(editor, editor.selection, 'block');
            var blockPath = _block3[1];
            var blockNode = _block3[0];
            var start = Editor.start(editor, blockPath);
            var _offset = start.offset;
            if (_offset === 0 && blockNode.children.length === 1) {
              return;
            }
          }
        }
        exec(command);
        break;
      case 'delete_fragment':
        var _Range$edges = Range.edges(editor.selection),
          _Range$edges2 = _slicedToArray(_Range$edges, 2),
          end = _Range$edges2[1];
        var voids = Editor.match(editor, end.path, 'void');
        var endVoid = voids ? voids : null;
        // do not unhange selection when end point is a void node when delete fragment,
        Editor.delete(editor, {
          hanging: endVoid ? true : false
        });
        return;
      case 'clear_format':
        clearBlockFormat(editor);
        clearMarkerFormat(editor);
        return;
      default:
        exec(command);
    }
  };
  return editor;
};
export default withMarkdownShortcut;